import { Component, computed, DestroyRef, inject, input, signal } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { StandardImageComponent } from "@components/standard-image/standard-image.component";
import { Product } from "@domain/product/product";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { map, of, switchMap } from "rxjs";

@Component({
  selector: "app-product-image",
  templateUrl: "./product-image.component.html",
  styleUrl: "./product-image.component.scss",
  imports: [StandardImageComponent],
  standalone: true,
})
export class ProductImageComponent {
  private readonly productService = inject(ProductService);
  private readonly destroyRef = inject(DestroyRef);

  readonly product = input<Product>();
  readonly productId = input<string>();
  readonly imageUrl = input<string>();
  readonly fallbackImageUrl = input<string>();
  readonly iconSize = input<string>("150px");

  protected readonly resolvedImageUrl = signal<string | undefined>(undefined);
  protected readonly resolvedFallbackImageUrl = signal<string | undefined>(undefined);

  constructor() {
    toObservable(computed(() => this.getInputs()))
      .pipe(switchMap((inputs) => this.resolveImage$(inputs)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ imageUrl, fallback }) => {
        this.resolvedImageUrl.set(imageUrl);
        if (imageUrl && fallback && imageUrl !== fallback) {
          this.resolvedFallbackImageUrl.set(fallback);
        } else {
          this.resolvedFallbackImageUrl.set(undefined);
        }
      });
  }

  private getInputs() {
    return {
      product: this.product(),
      productId: this.productId(),
      imageUrl: this.imageUrl(),
      fallbackImageUrl: this.fallbackImageUrl(),
    };
  }

  private resolveImage$(inputs: { product?: Product; productId?: string; imageUrl?: string; fallbackImageUrl?: string }) {
    const fallback = inputs.fallbackImageUrl;

    if (inputs.imageUrl) {
      return of({ imageUrl: inputs.imageUrl, fallback });
    }

    if (inputs.product?.cantoImageId) {
      return of({ imageUrl: ProductImage.getCantoSourceUrl(inputs.product.cantoImageId), fallback });
    }

    const resolvedProductId = inputs.productId ?? inputs.product?.id;
    if (!resolvedProductId) {
      return of({ imageUrl: fallback, fallback });
    }

    const cachedUrl = this.productService.getCantoImageUrlFromCache(resolvedProductId);
    if (cachedUrl) {
      return of({ imageUrl: cachedUrl, fallback });
    }

    return this.productService.getCantoImageUrl(resolvedProductId).pipe(map((url) => ({ imageUrl: url ?? fallback, fallback })));
  }
}
